import React, { useState, useMemo, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Grid, 
  Paper, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions, 
  CircularProgress,
  IconButton,
  Stack,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction
} from '@mui/material';
import { Mic, MicOff, Videocam, VideocamOff, CallEnd, Settings } from '@mui/icons-material';
import AgoraRTC, { IAgoraRTCClient, IMicrophoneAudioTrack, ICameraVideoTrack } from "agora-rtc-sdk-ng";
import { supabase } from '../../Supabase/supabaseRealtimeClient';
import {
  AgoraRTCProvider,
  useJoin,
  useRemoteUsers,
  RemoteUser,
  LocalVideoTrack,
  useRTCClient,
  useClientEvent,
  useIsConnected,
} from "agora-rtc-react";

interface VideoRoomProps {
  roomId: string;
  userId: string;
  onLeave: () => void;
  onEndCall: () => void;
  token?: string | null;
  originalUserId?: string;
  children?: React.ReactNode;
}

// Interface auxiliar para as props internas
interface InnerCallProps extends VideoRoomProps {
  numericUid: number;
}

const AgoraVideoCall: React.FC<InnerCallProps> = ({ 
  roomId, 
  numericUid, 
  userId, 
  onLeave, 
  onEndCall, 
  token, 
  originalUserId,
  children
}) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const appId = process.env.REACT_APP_AGORAIO_APP_ID || '';
  
  // Manual Track Management
  const [localMicrophoneTrack, setLocalMicrophoneTrack] = useState<IMicrophoneAudioTrack | null>(null);
  const [localCameraTrack, setLocalCameraTrack] = useState<ICameraVideoTrack | null>(null);

  useEffect(() => {
    let audioTrack: IMicrophoneAudioTrack | null = null;
    let videoTrack: ICameraVideoTrack | null = null;
    let isMounted = true;

    const initTracks = async () => {
        try {
            console.log('[VideoRoom] Initializing tracks...');
            
            // Create Audio Track
            const newAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({ 
                AEC: true, 
                ANS: true 
            });
            if (isMounted) {
                audioTrack = newAudioTrack;
                // Ensure enabled/unmuted initially
                await audioTrack.setEnabled(true);
                await audioTrack.setMuted(false);
                setLocalMicrophoneTrack(audioTrack);
                console.log('[VideoRoom] Mic track created:', audioTrack.getTrackId());
            } else {
                newAudioTrack.stop();
                newAudioTrack.close();
            }

            // Create Video Track
            const newVideoTrack = await AgoraRTC.createCameraVideoTrack({
                encoderConfig: "720p_1"
            });
            if (isMounted) {
                videoTrack = newVideoTrack;
                // Ensure enabled/unmuted initially
                await videoTrack.setEnabled(true);
                await videoTrack.setMuted(false);
                setLocalCameraTrack(videoTrack);
                console.log('[VideoRoom] Cam track created:', videoTrack.getTrackId());
            } else {
                newVideoTrack.stop();
                newVideoTrack.close();
            }

        } catch (error) {
            console.error('[VideoRoom] Error initializing tracks:', error);
        }
    };

    initTracks();

    return () => {
        console.log('[VideoRoom] Cleaning up tracks...');
        isMounted = false;
        
        const stopAndClose = (track: any) => {
            if (track) {
                try {
                    track.stop();
                    track.close();
                } catch (e) {
                    console.warn('[VideoRoom] Error closing track:', e);
                }
            }
        };

        stopAndClose(audioTrack);
        stopAndClose(videoTrack);
        
        // Also cleanup state tracks if they are different from local variables
        if (localMicrophoneTrack && localMicrophoneTrack !== audioTrack) {
             stopAndClose(localMicrophoneTrack);
        }
        if (localCameraTrack && localCameraTrack !== videoTrack) {
             stopAndClose(localCameraTrack);
        }

        console.log('[VideoRoom] Tracks stopped and closed.');
    };
  }, []); // Removed localMicrophoneTrack, localCameraTrack from dependencies to prevent infinite loop

  // Monitor events like the example
  const client = useRTCClient();

  useClientEvent(client, "user-published", async (user: any, mediaType: "audio" | "video") => {
    console.log('[VideoRoom] User published:', user.uid, 'Media:', mediaType);
    try {
      await client.subscribe(user, mediaType);
      console.log('[VideoRoom] Subscribed to:', user.uid, 'Media:', mediaType);
    } catch (err) {
      console.error('[VideoRoom] Subscribe failed:', err);
    }
  });

  useClientEvent(client, "user-unpublished", (user: any, mediaType: "audio" | "video") => {
    console.log('[VideoRoom] User unpublished:', user.uid, 'Media:', mediaType);
  });

  useClientEvent(client, "user-joined", (user: any) => {
    console.log('[VideoRoom] User joined:', user.uid);
  });

  useClientEvent(client, "user-left", (user: any) => {
    console.log('[VideoRoom] User left:', user.uid);
  });

  // Publish tracks directly like the example
  // usePublish([localMicrophoneTrack, localCameraTrack]);
  const isConnected = useIsConnected();
  const [audioPublished, setAudioPublished] = useState(false);
  const [videoPublished, setVideoPublished] = useState(false);

  useEffect(() => {
    console.log('[VideoRoom] Publish Effect Check:', {
        isConnected,
        hasMic: !!localMicrophoneTrack,
        hasCam: !!localCameraTrack,
        audioPublished,
        videoPublished,
    });

    let timeoutId: NodeJS.Timeout;

    const publishTracks = async () => {
        if (!isConnected) {
            console.log('[VideoRoom] Not connected yet. Waiting for join...');
            return;
        }
        
        console.log('[VideoRoom] Ready to publish. Checking tracks...');

        // Pequeno delay para garantir estabilidade da conexão
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
            // Publish Audio
            if (localMicrophoneTrack && !audioPublished) {
                // Force unmute and enable again just to be 100% sure before publishing
                if (localMicrophoneTrack.muted) {
                    console.log('[VideoRoom] Unmuting mic before publish');
                    await localMicrophoneTrack.setMuted(false);
                }
                if (!localMicrophoneTrack.enabled) {
                    await localMicrophoneTrack.setEnabled(true);
                }
                
                console.log('[VideoRoom] Publishing Mic track...');
                await client.publish(localMicrophoneTrack as any);
                console.log('[VideoRoom] Mic Published successfully.');
                setAudioPublished(true);
            }

            // Publish Video
            if (localCameraTrack && !videoPublished) {
                if (localCameraTrack.muted) {
                    console.log('[VideoRoom] Unmuting cam before publish');
                    await localCameraTrack.setMuted(false);
                }
                if (!localCameraTrack.enabled) {
                    await localCameraTrack.setEnabled(true);
                }
                
                console.log('[VideoRoom] Publishing Cam track...');
                await client.publish(localCameraTrack as any);
                console.log('[VideoRoom] Cam Published successfully.');
                setVideoPublished(true);
            }

        } catch (error) {
            console.error('[VideoRoom] Publish error:', error);
            // Retry after 2 seconds if failed
            timeoutId = setTimeout(publishTracks, 2000);
        }
    };

    publishTracks();
    
    return () => {
        if (timeoutId) clearTimeout(timeoutId);
    };
    
  }, [isConnected, localMicrophoneTrack, localCameraTrack, client, audioPublished, videoPublished]);

  // Join Channel
  useJoin(
    {
      appid: appId,
      channel: roomId,
      token: token || null,
      uid: numericUid, // Passando o ID numérico gerado
    },
    true, // ready
  );

  /* Removed duplicate publish logic */
  
  // Remote users
  const remoteUsers = useRemoteUsers();

  // Monitor mute status (using state to force re-render on change)
  const [micMuted, setMicMuted] = useState(false);
  const [camMuted, setCamMuted] = useState(false);

  useEffect(() => {
    if (localMicrophoneTrack) {
        setMicMuted(localMicrophoneTrack.muted);
        // Force enabled and unmute
        if (!localMicrophoneTrack.enabled) {
           console.log('[VideoRoom] Mic disabled, enabling...');
           localMicrophoneTrack.setEnabled(true);
        }
        if (localMicrophoneTrack.muted) {
          console.log('[VideoRoom] Mic initially muted, unmuting...');
          localMicrophoneTrack.setMuted(false);
        }
    }
    if (localCameraTrack) {
        setCamMuted(localCameraTrack.muted);
        console.log('[VideoRoom] Local Cam Track:', localCameraTrack.getTrackLabel(), 'Muted:', localCameraTrack.muted, 'Enabled:', localCameraTrack.enabled);
        
        // Force enabled and unmute
        if (!localCameraTrack.enabled) {
           console.log('[VideoRoom] Cam disabled, enabling...');
           localCameraTrack.setEnabled(true);
        }

        if (localCameraTrack.muted) {
          console.log('[VideoRoom] Cam initially muted, unmuting...');
          localCameraTrack.setMuted(false);
        }
    }
  }, [localMicrophoneTrack, localCameraTrack]);

  const toggleMic = async () => {
    if (localMicrophoneTrack) {
        await localMicrophoneTrack.setMuted(!micMuted);
        setMicMuted(localMicrophoneTrack.muted);
    }
  };

  const toggleCam = async () => {
    if (localCameraTrack) {
        await localCameraTrack.setMuted(!camMuted);
        setCamMuted(localCameraTrack.muted);
    }
  };

  const isPublished = audioPublished || videoPublished;
  
  const forcePublish = async () => {
     if (!client) return;
     try {
         if (localMicrophoneTrack && !audioPublished) {
             console.log('[VideoRoom] Force publishing Mic...');
             await client.publish(localMicrophoneTrack as any);
             setAudioPublished(true);
         }
         if (localCameraTrack && !videoPublished) {
             console.log('[VideoRoom] Force publishing Cam...');
             await client.publish(localCameraTrack as any);
             setVideoPublished(true);
         }
     } catch (e) {
         console.error('[VideoRoom] Force publish failed:', e);
     }
   };

  const [remoteUserName, setRemoteUserName] = useState<string>('Usuário Remoto');

  // Fetch Remote User Name
  useEffect(() => {
    const fetchRemoteName = async () => {
        // Extract call ID from room-123 format
        const parts = roomId.split('-');
        const callId = parts.length > 1 ? parts[1] : null;

        if (!callId) return;

        try {
            // Nota: Se 'users' não funcionar, tente 'user' dependendo da sua view no Supabase.
            // Assumindo 'users' conforme tentativa anterior, mas adicionando log de erro se falhar.
            const { data: atendimento, error } = await supabase
                .from('video_atendimentos')
                .select(`
                    *,
                    cliente:cliente(nome),
                    medico:medicos(nome)
                `)
                .eq('id', callId)
                .single();

            if (error) {
                console.error('[VideoRoom] Error fetching call details:', error);
                return;
            }

            if (atendimento) {
                // Lógica Robusta de Papéis
                let myRole = 'unknown';

                // 1. Sou o Cliente?
                if (atendimento.cliente_id) {
                     const { data: myClientMatch } = await supabase
                        .from('cliente')
                        .select('id')
                        .eq('user_id', userId)
                        .eq('id', atendimento.cliente_id)
                        .maybeSingle();
                     
                     if (myClientMatch) myRole = 'cliente';
                }

                // 2. Sou o Médico?
                if (myRole === 'unknown' && atendimento.medico_id) {
                     const { data: myMedicoMatch } = await supabase
                        .from('medicos')
                        .select('id')
                        .eq('user_id', userId)
                        .eq('id', atendimento.medico_id)
                        .maybeSingle();
                     
                     if (myMedicoMatch) myRole = 'medico';
                }

                // 3. Sou o Atendente?
                if (myRole === 'unknown' && atendimento.atendente_id === userId) {
                    myRole = 'atendente';
                }

                // Fallback: Tenta descobrir se sou cliente em geral (caso o ID do cliente no chamado seja diferente por algum erro)
                if (myRole === 'unknown') {
                    const { data: isAnyClient } = await supabase
                       .from('cliente')
                       .select('id')
                       .eq('user_id', userId)
                       .maybeSingle();
                    if (isAnyClient) myRole = 'cliente';
                }

                console.log('[VideoRoom] Role detected:', myRole);

                // Definir Nome Remoto
                if (myRole === 'cliente') {
                    if (atendimento.status === 'em_consulta') {
                        setRemoteUserName(atendimento.medico?.nome || 'Médico');
                    } else {
                        // Como o join com 'users' pode ser problemático, vamos pegar o nome do atendente de outra forma se possível,
                        // ou usar um fallback genérico 'Atendente'.
                        // Se quiser tentar buscar o nome do atendente separadamente:
                        if (atendimento.atendente_id) {
                             // Tentar buscar perfil público ou metadados se disponível.
                             // Por enquanto, 'Atendente' é seguro.
                             setRemoteUserName('Atendente');
                        } else {
                             setRemoteUserName('Aguardando Profissional...');
                        }
                    }
                } else {
                    // Sou Profissional (Médico ou Atendente) -> Vejo o Paciente
                    setRemoteUserName(atendimento.cliente?.nome || 'Paciente');
                }
            }
        } catch (error) {
            console.error('[VideoRoom] Error fetching remote name:', error);
        }
    };
    
    fetchRemoteName();
  }, [roomId, userId]);

  // Monitor call status from Supabase to auto-quit
  useEffect(() => {
      // Extract call ID from room-123 format
      const parts = roomId.split('-');
      const callId = parts.length > 1 ? parts[1] : null;
      
      if (!callId) return;

      console.log('[VideoRoom] Monitoring call status for ID:', callId);

      const channel = supabase
        .channel(`room_monitor_${callId}`)
        .on(
          'postgres_changes',
          { 
            event: '*', 
            schema: 'public', 
            table: 'video_atendimentos', 
            filter: `id=eq.${callId}` 
          },
          (payload: any) => {
            console.log('[VideoRoom] Call update:', payload);
            
            // If deleted or status changed to finished
            if (payload.eventType === 'DELETE') {
               console.log('[VideoRoom] Call deleted. Exiting...');
               alert('A chamada foi encerrada.');
               onLeave();
            } else if (payload.eventType === 'UPDATE') {
               const newStatus = payload.new.status;
               if (newStatus === 'finalizado') {
                  console.log('[VideoRoom] Call finished. Exiting...');
                  // Alert removido para fluxo mais suave, a tela deve mudar automaticamente
                  // alert('A chamada foi finalizada.');
                  onLeave();
               }
            }
          }
        )
        .subscribe();

      return () => {
        console.log('[VideoRoom] Unsubscribing monitor...');
        supabase.removeChannel(channel);
      };
  }, [roomId, onLeave]);

  return (
    <Box sx={{ p: 2, height: '100%', position: 'relative', color: 'black' }}>
      {/* Debug Overlay */}
      {/* <Typography variant="caption" sx={{ position: 'absolute', top: 10, left: 10, opacity: 0.7, zIndex: 1000, bgcolor: 'rgba(0,0,0,0.6)', p: 1, borderRadius: 1, pointerEvents: 'none' }}>
        Room: {roomId} | Remotes: {remoteUsers.length} | Me (Int): {numericUid}
        <br />
        Status: {isPublished ? '📡 Publishing' : '⏳ Waiting Tracks...'}
        <br />
        Conn: {isConnected ? '✅ Connected' : '❌ Disconnected'}
        <br />
        Mic: {localMicrophoneTrack ? (micMuted ? 'Muted' : 'OK') : 'Wait'} | Cam: {localCameraTrack ? (camMuted ? 'Muted' : 'OK') : 'Wait'}
      </Typography> */}
      
      {/* {!isPublished && isConnected && (localCameraTrack || localMicrophoneTrack) && (
          <Button 
            size="small" 
            variant="contained" 
            color="warning" 
            onClick={forcePublish}
            sx={{ position: 'absolute', top: 80, left: 10, zIndex: 1001 }}
          >
            Forçar Publicação
          </Button>
      )} */}

      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Remote Video (Main) */}
        <Grid size={{ xs: 12, md: 8 }} sx={{ height: { xs: '60%', md: '100%' } }}>
          <Paper sx={{ p: 0, bgcolor: 'black', height: '100%', borderRadius: 2, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography sx={{ position: 'absolute', top: 5, right: 5, zIndex: 10, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', px: 1, fontSize: '0.8rem', borderRadius: 1 }}>
              {remoteUserName}
            </Typography>
            {remoteUsers.length > 0 ? (
              <Box sx={{ width: '100%', height: '100%' }}>
                <RemoteUser 
                  user={remoteUsers[0]} 
                  playVideo={true}
                  playAudio={true} // O componente RemoteUser já gerencia o áudio deste usuário
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                />
                <Box sx={{ position: 'absolute', bottom: 10, left: 10, bgcolor: 'rgba(0,0,0,0.5)', px: 1, borderRadius: 1 }}>
                   <Typography sx={{ position: 'absolute', top: 5, right: 5, zIndex: 10, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', px: 1, fontSize: '0.8rem', borderRadius: 1 }}>
                    {remoteUsers[0].hasVideo ? '' : ' (Sem Vídeo)'}
                    {remoteUsers[0].hasAudio ? '' : ' (Mudo)'}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="h6" color="text.secondary">
                  Aguardando conexão...
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Local Video (Secondary / Controls) */}
        <Grid size={{ xs: 12, md: 4 }} sx={{ height: { xs: '40%', md: '100%' }, display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ bgcolor: '#000', height: '250px', mb: 2, borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
             <Typography sx={{ position: 'absolute', top: 5, right: 5, zIndex: 10, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', px: 1, fontSize: '0.8rem', borderRadius: 1 }}>
               Você
             </Typography>
            {localCameraTrack ? (
                <LocalVideoTrack track={localCameraTrack as any} play style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <CircularProgress size={20} />
                </Box>
            )}
            
            {/* SpeedDial Controls - Movido para fora do box do video local mas dentro da grid */}
          </Paper>
          
          <Box sx={{ mt: 'auto', p: 2 }}>
            {children && (
              <Box sx={{ mb: 2 }}>
                {children}
              </Box>
            )}
            <Button variant="outlined" color="inherit" fullWidth onClick={onLeave} startIcon={<CallEnd />} sx={{ mb: 2, borderColor: 'rgba(0,0,0,0.3)', color: 'black' }}>
              Sair do Atendimento
            </Button>
            <Button variant="contained" color="error" fullWidth onClick={() => setOpenConfirm(true)}>
              Encerrar Atendimento
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
      >
        <DialogTitle>Encerrar Atendimento?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja encerrar por completo este atendimento? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancelar</Button>
          <Button onClick={() => { setOpenConfirm(false); onEndCall(); }} color="error" autoFocus>
            Encerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Controls */}
      <SpeedDial
        ariaLabel="Controles de Mídia"
        sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1300 }}
        icon={<Settings />}
      >
        <SpeedDialAction
          key="mic"
          icon={micMuted ? <MicOff /> : <Mic />}
          tooltipTitle={micMuted ? 'Ativar Microfone' : 'Silenciar Microfone'}
          onClick={toggleMic}
          FabProps={{
             sx: { bgcolor: micMuted ? 'error.main' : 'primary.main', color: 'white', '&:hover': { bgcolor: micMuted ? 'error.dark' : 'primary.dark' } }
          }}
        />
        <SpeedDialAction
          key="cam"
          icon={camMuted ? <VideocamOff /> : <Videocam />}
          tooltipTitle={camMuted ? 'Ativar Câmera' : 'Desativar Câmera'}
          onClick={toggleCam}
          FabProps={{
             sx: { bgcolor: camMuted ? 'error.main' : 'primary.main', color: 'white', '&:hover': { bgcolor: camMuted ? 'error.dark' : 'primary.dark' } }
          }}
        />
      </SpeedDial>
    </Box>
  );
};

// Helper para converter string (UUID) em ID numérico para o Agora
const stringToUint32 = (str: string): number => {
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  // Force 16-bit integer (1 to 60000) to avoid INVALID_PARAMS error [0, 65535]
  // Using modulo 60000 ensures we stay well within the safe range
  return (Math.abs(hash) % 60000) + 1;
};

const VideoRoom: React.FC<VideoRoomProps> = (props) => {
  // Create client directly using AgoraRTC
  // We don't use useRTCClient here because we are outside the AgoraRTCProvider context
  // The client is passed TO the provider.
  const agoraClient = useMemo(() => {
     const client = AgoraRTC.createClient({ codec: "vp8", mode: "rtc" });
     // Add global error handler
     client.on("exception", (err) => {
        console.warn("[VideoRoom] Agora Exception:", err);
     });
     
     client.on("connection-state-change", (curState, revState) => {
        console.log("[VideoRoom] Connection State Change:", revState, "->", curState);
     });

     return client;
  }, []);

  // Ensure client leaves on unmount
  useEffect(() => {
      return () => {
          if (agoraClient) {
              console.log('[VideoRoom] Client leaving channel...');
              // Unpublish local tracks before leaving if they exist
              if (agoraClient.localTracks && agoraClient.localTracks.length > 0) {
                  agoraClient.localTracks.forEach(track => {
                      track.stop();
                      track.close();
                  });
                  agoraClient.unpublish();
              }
              agoraClient.leave().catch(err => console.error('[VideoRoom] Leave error:', err));
          }
      };
  }, [agoraClient]);
  
  const numericUid = useMemo(() => stringToUint32(props.userId), [props.userId]);
  
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchToken = async () => {
      try {
        setLoading(true);
        console.log('[VideoRoom] Fetching token for:', props.roomId, 'UID:', numericUid);
        
        const { data, error } = await supabase.functions.invoke('agora-token', {
          body: { 
            channelName: props.roomId, 
            uid: numericUid, 
            role: 'publisher' 
          }
        });

        if (!isMounted) return;

        if (error) {
          console.error('[VideoRoom] Supabase Error:', error);
          setToken(null);
        } else if (data?.token) {
          console.log('[VideoRoom] Token received.');
          setToken(data.token);
        } else {
          console.warn('[VideoRoom] No token in response.');
          setToken(null);
        }
      } catch (e) {
        console.error('[VideoRoom] Exception:', e);
        if (isMounted) setToken(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchToken();

    return () => { isMounted = false; };
  }, [props.roomId, numericUid]);

  if (!process.env.REACT_APP_AGORAIO_APP_ID) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error" variant="h6">Configuração Faltante</Typography>
        <Typography>REACT_APP_AGORAIO_APP_ID não encontrado.</Typography>
        <Button onClick={props.onLeave} sx={{ mt: 2 }}>Voltar</Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '400px', bgcolor: '#121212', color: 'white' }}>
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2 }}>Preparando sala segura...</Typography>
      </Box>
    );
  }

  // 2. AgoraRTCProvider receives the raw client object
  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: '600px' }}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <AgoraRTCProvider client={agoraClient as any}>
            <AgoraVideoCall 
                {...props} 
                numericUid={numericUid} 
                token={token}
            >
                {props.children}
            </AgoraVideoCall>
        </AgoraRTCProvider>
    </Box>
  );
};

export default VideoRoom;